import { ListOutput } from '@allocab/allocab-commons-shared-types';
import { SearchTripManagerInput, TripDriverWithDriverOutput } from '@allocab/allocab-driver-shared-types';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { queryKeys } from '../services/datastore/queryKeys';
import { DEFAULT_CACHE_TIME, queryClient } from '../services/datastore/react-query';
import { InfiniteQueryOptions } from '../services/datastore/types';
import { driverApi } from '../services/network/Api';

export const TRIPS_LIMIT = 10;

export type Input = Omit<SearchTripManagerInput, 'offset' | 'limit'> & { limit?: number };

export const fetchTrips = (input: SearchTripManagerInput) =>
  driverApi.post<SearchTripManagerInput, TripDriverWithDriverOutput, 'list'>('/trip/v1/search', input);

export const useFethTripsPage = (input: Input, pageNumber = 1) => {
  const limit = input.limit ?? TRIPS_LIMIT;
  const apiInput: SearchTripManagerInput = { ...input, limit, offset: (pageNumber - 1) * limit };

  return useQuery(queryKeys.user.fetchTrips(apiInput), () => fetchTrips(apiInput), {
    keepPreviousData: true,
  });
};

export const prefetchTripsPage = (input: Input, pageNumber = 1) => {
  const limit = input.limit ?? TRIPS_LIMIT;
  const apiInput: SearchTripManagerInput = { ...input, limit, offset: (pageNumber - 1) * limit };

  queryClient.prefetchQuery(queryKeys.user.fetchTrips(apiInput), () => fetchTrips(apiInput), {
    staleTime: DEFAULT_CACHE_TIME,
  });
};

export const useInfiniteFetchTrips = (
  input: Input,
  options: InfiniteQueryOptions<typeof queryKeys.user.fetchInfiniteTrips, ListOutput<TripDriverWithDriverOutput>> = {},
) => {
  const fetchTripsQuery = useInfiniteQuery(
    queryKeys.user.fetchInfiniteTrips(input),
    ({ pageParam = 0 }: { pageParam?: number }) =>
      fetchTrips({ limit: TRIPS_LIMIT, offset: pageParam, orderBy: 'resume.departureAt', ...input }),
    {
      getNextPageParam: (lastPage, allPages) =>
        lastPage.nbItems < TRIPS_LIMIT ? undefined : TRIPS_LIMIT * allPages.length,
      ...options,
    },
  );

  return fetchTripsQuery;
};
